import React from 'react'
import DialogContent from '@material-ui/core/DialogContent'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import InsertChartOutlinedIcon from 'components/material-symbol/icons/InsertChartOutlined'
import SubviewPageHeader from '../subview-page-header'
import { DateTime } from 'luxon'
import { daysUntil } from 'utils/dateUtils'
import { useSupportContactActions } from 'lib/support'
import { currency } from 'lib/utils/string'
import { centsToDollars } from 'lib/utils/number'
import { useStyles } from './styles'

const StatCard = ({ label, value, badge, classes }) => (
  <Box className={classes.statCard}>
    <Typography className={classes.statLabel}>{label}</Typography>
    <Typography className={classes.statValue}>{value}</Typography>
    {badge ? (
      <Box className={classes.statBadgeSpacer}>
        <Chip
          label={badge}
          size="small"
          classes={{
            root: classes.statBadge,
            label: classes.statBadgeLabel,
          }}
        />
      </Box>
    ) : null}
  </Box>
)

const formatEventDate = (startDateTime, timezone) => {
  const dt = DateTime.fromISO(startDateTime).setZone(timezone)
  if (!dt.isValid) return null
  return dt.toFormat('LLL d')
}

const getAttendanceBadge = (count, maxAttendance) => {
  const max =
    maxAttendance != null && maxAttendance !== '' ? Number(maxAttendance) : null

  if (max != null && !Number.isNaN(max) && max > 0) {
    return `${count}/${max}`
  }

  return `${count} registered`
}

const getRevenueBadge = revenueInCents => {
  if (!revenueInCents || revenueInCents === 0) {
    return 'No payments yet'
  }
  return null
}

const getDeadlineBadge = daysLeft => {
  if (daysLeft <= 3) return 'Closing soon'
  return `${daysLeft} days left`
}

export const EventStatsContent = ({ setIsOpen, event, embedded = false }) => {
  const {
    title,
    registrationCount,
    totalEnrolled,
    startDateTime,
    registrationCloseDateTime,
    revenueInCents,
    maxAttendance,
    timezone,
  } = event
  const { composeSupportMessage } = useSupportContactActions()
  const numberOfParticipants = totalEnrolled ?? registrationCount ?? 0
  const revenue = centsToDollars(revenueInCents)
  const classes = useStyles({ embedded })
  const daysLeft = daysUntil(registrationCloseDateTime)
  const attendanceBadge = getAttendanceBadge(
    numberOfParticipants,
    maxAttendance,
  )
  const revenueBadge = getRevenueBadge(revenueInCents)
  const deadlineBadge = getDeadlineBadge(daysLeft)
  const eventDate = formatEventDate(startDateTime, timezone)

  const handleFeedback = () =>
    composeSupportMessage(
      "I'd like to suggest group events stats! Please contact me with more information.",
    )

  const statsCards = [
    {
      key: 'revenue',
      label: 'Revenue',
      value: currency(revenue),
      badge: revenueBadge,
    },
    ...(!event.externalRegistrationUrl
      ? [
        {
          key: 'attendees',
          label: 'Attendees',
          value: String(numberOfParticipants),
          badge: attendanceBadge,
        },
      ]
      : []),
    {
      key: 'deadline',
      label: 'Reg. deadline',
      value: `${daysLeft} days`,
      badge: deadlineBadge,
    },
    {
      key: 'event-date',
      label: 'Event date',
      value: eventDate,
    },
  ]

  return (
    <>
      <SubviewPageHeader
        eventTitle={title}
        pageTitle="Stats"
        onBack={() => setIsOpen('detail')}
      />
      <Box className={classes.statsSection}>
        <Grid container spacing={2} className={classes.statsGrid}>
          {statsCards.map(card => (
            <Grid
              item
              xs={12}
              sm={6}
              key={card.key}
              className={classes.statsGridItem}
            >
              <StatCard {...card} classes={classes} />
            </Grid>
          ))}
        </Grid>
        <Box
          className={classes.feedbackRow}
          onClick={handleFeedback}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleFeedback()
            }
          }}
        >
          <InsertChartOutlinedIcon />
          <Typography variant="body2" component="span">
            Tell us what stats you'd like to see
          </Typography>
        </Box>
      </Box>
    </>
  )
}

const EventStats = ({ setIsOpen, event, embedded = false }) => {
  const classes = useStyles({ embedded })

  if (embedded) {
    return (
      <Box className={classes.embeddedRoot}>
        <EventStatsContent {...{ setIsOpen, event, embedded }} />
      </Box>
    )
  }

  return (
    <DialogContent className={classes.root}>
      <EventStatsContent {...{ setIsOpen, event, embedded }} />
    </DialogContent>
  )
}

export default EventStats
